import React, { useState, useEffect } from "react";
import { Row, Col, Accordion, Badge, InputGroup } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import usePermissions from "hooks/usePermissions";

// import component
import Alert from "components/molecules/Alert/Alert";
import Dialog from "components/organisms/Dialog/Dialog";
import Input from "components/atoms/Input/Input";
import Button from "components/atoms/Button/Button";
import Textarea from "components/atoms/Textarea/Textarea";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import { getUserRoleDetails, getNewUserRoles, createNewUserRole, updateUserRoleDetails } from "api";
import errorToast from "helpers/errorToast";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import { toast } from "react-toastify";
import DeleteUserRole from "./DeleteUserRole";

const EditUserRole = function ({ showEditRoleDialog, closeEditRoleDialog, id, count, refetch, viewOnly }) {
  const { permissions } = usePermissions();
  const basePermissions = JSON.parse(permissions || "{}");
  const canDeleteUserRole = basePermissions?.permissions?.admin_settings?.edit_user_roles;

  const [selectedUser, setSelectedUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [roleError, setRoleError] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [roleTypes, setRoleTypes] = useState([]);

  const formatUsers = (selectedUsers, availableUsers) => {
    const selectedUsersWithIsActive = selectedUsers.map((user) => ({ ...user, is_active: true }));
    const availableUsersWithIsActive = availableUsers.map((user) => ({ ...user, is_active: false }));
    const allUsers = [...selectedUsersWithIsActive, ...availableUsersWithIsActive];
    return allUsers;
  };

  function getUserData(id) {
    setIsLoading(true);
    if (id) {
      getUserRoleDetails(id)
        .then((res) => {
          if (res.data) {
            setSelectedUser(res.data);
            setValue("role_name", res.data.name);
            setValue("role_type", res.data.type);
            setValue("description", res.data.description);
            const formattedUsers = formatUsers(res.data.selected_users, res.data.available_users);
            setAllUsers(formattedUsers);
            setFilteredUsers(formattedUsers);
          }
        })
        .catch((err) =>
          errorToast(
            err,
            intl.formatMessage({
              id: "settings.user-role.couldNotRetrieveUserRoleDetail",
              defaultMessage: "Could not retrieve user role details. Please try again later.",
            }),
          ),
        );
      getNewUserRoles().then((res) => {
        if (res.data) {
          if (res.data.role_type !== null) {
            setRoleTypes(res.data.role_type);
          }
        }
      });
    } else {
      getNewUserRoles()
        .then((res) => {
          if (res.data) {
            setSelectedUser(res.data);
            setAllUsers(res.data.available_users.map((user) => ({ ...user, is_active: false })));
            setFilteredUsers(res.data.available_users.map((user) => ({ ...user, is_active: false })));
            if (res.data.role_type !== null) {
              setRoleTypes(res.data.role_type);
            }
          }
        })
        .catch((err) =>
          errorToast(
            err,
            intl.formatMessage({
              id: "settings.user-role.couldNotRetrieveUserRoleDetail",
              defaultMessage: "Could not retrieve user role details. Please try again later.",
            }),
          ),
        )
        .finally(() => setIsLoading(false));
    }
  }

  useEffect(() => {
    getUserData(id);
  }, [id]);

  const intl = useIntl();
  const methods = useForm();
  const { setValue, getValues, handleSubmit } = methods;
  const [activeAccordion, setActiveAccordion] = useState(0);

  useEffect(() => {
    if (userSearch) {
      const timeoutId = setTimeout(() => {
        const filteredUsers = allUsers.filter((user) => {
          const fullName = `${user.first_name} ${user.last_name}`;
          return fullName.toLowerCase()?.includes(userSearch.toLowerCase());
        });
        setFilteredUsers(filteredUsers);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [userSearch]);

  useEffect(() => {
    if (Object.keys(selectedUser).length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(selectedUser.permissions).forEach(([key, value]) => {
      value.forEach((item) => {
        setValue(`permissions.${item.value}`, item.is_active);
      });
    });
    setIsLoading(false);
  }, [selectedUser]);

  const handleSearchUser = (e) => {
    const { value } = e.target;
    setUserSearch(value);
    if (value === "") setFilteredUsers(allUsers);
  };

  const onSubmit = (data) => {
    if (viewOnly) return;
    setIsSubmitting(true);
    const permissions = Object.entries(data.permissions).reduce((acc, [key, value]) => {
      if (value) acc.push(key);
      return acc;
    }, []);
    const selected_users = allUsers.map((user) => user.is_active === true && user.id).filter((user) => user);
    const payload = {
      name: data.role_name,
      role_type: data.role_type,
      description: data.description,
      permissions,
      selected_users,
    };
    if (data.role_type === undefined || data.role_type === null) setRoleError(true);
    if (permissions.length === 0) setPermissionsError(true);
    if (id) {
      updateUserRoleDetails(id, payload)
        .then(() => {
          toast("Role updated successfully", { type: "success" });
          closeEditRoleDialog();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    } else {
      createNewUserRole(payload)
        .then(() => {
          toast("Role created successfully", { type: "success" });
          closeEditRoleDialog();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    }
  };

  return (
    <Dialog
      show={showEditRoleDialog}
      isLoading={isLoading}
      size="md"
      title={intl.formatMessage({ id: "settings.user-role.roleDetails", defaultMessage: "Role details" })}
      onHide={closeEditRoleDialog}
    >
      {isLoading ? (
        <div> Loading </div>
      ) : (
        <FormProvider {...methods}>
          {showDelete ? (
            <DeleteUserRole
              closeEditRoleDialog={closeEditRoleDialog}
              setShowDelete={setShowDelete}
              count={count}
              id={id}
              refetch={refetch}
            />
          ) : (
            <div>
              {id && !selectedUser.can_update && (
                <Row className="mb-30">
                  <Alert className="mb-0" variant="warning">
                    {intl.formatMessage({
                      id: "settings.user-role.cannotEditOwnRoleMsg",
                      defaultMessage: "You are a member of this role. Therefore, you are not able to edit this role.",
                    })}
                  </Alert>
                </Row>
              )}

              <div className="d-flex" style={{ padding: "16px 0", borderBottom: "1px solid #F1F3F4" }}>
                <Col lg={4} className="p-0">
                  <div className="font-b1 font-weight-bold required">
                    <FormattedMessage id="general.name" defaultMessage="Name" />
                  </div>
                </Col>
                <Col lg={8} className="p-0">
                  <Input
                    type="text"
                    required
                    name="role_name"
                    disabled={(id && !selectedUser.can_update) || viewOnly}
                    placeholder={intl.formatMessage({
                      id: "settings.user-role.roleName",
                      defaultMessage: "Role name",
                    })}
                    className="rounded-input input-width-md"
                  />
                </Col>
              </div>

              <div className="d-flex" style={{ padding: "16px 0", borderBottom: "1px solid #F1F3F4" }}>
                <Col lg={4} className="p-0">
                  <div className="font-b1 font-weight-bold">
                    <FormattedMessage id="settings.user-role.description" defaultMessage="Description" />
                  </div>
                </Col>
                <Col lg={8} className="p-0">
                  <Textarea
                    style={{
                      border: "1px solid black",
                      borderRadius: "13px !important",
                    }}
                    name="description"
                    className="rounded-textarea"
                    rows={2}
                    placeholder={intl.formatMessage({
                      id: "settings.user-role.description",
                      defaultMessage: "Description",
                    })}
                    disabled={viewOnly}
                  />
                </Col>
              </div>

              <div className="d-flex" style={{ padding: "16px 0", borderBottom: "1px solid #F1F3F4" }}>
                <Col lg={4} className="p-0">
                  <div className="font-b1 font-weight-bold required">
                    <FormattedMessage id="settings.user-role.roleType" defaultMessage="Role type" />
                  </div>
                </Col>
                <Col lg={8} className="p-0">
                  <Dropdown
                    useHookForm
                    name="role_type"
                    options={roleTypes}
                    defaultValue={
                      roleTypes.filter((type) => type.value === getValues("role_type"))[0]?.label || "Please select"
                    }
                    onChange={(item) => setValue("role_type", item.value)}
                    disabled={viewOnly}
                  />
                  {roleError && (
                    <div className="text-danger font-b3 mt-1">
                      <FormattedMessage id="general.requiredField" defaultMessage="This is a required field." />
                    </div>
                  )}
                </Col>
              </div>

              <div className="d-flex" style={{ padding: "16px 0", borderBottom: "1px solid #F1F3F4" }}>
                <Col lg={4} className="p-0">
                  <div className="font-b1 font-weight-bold required">
                    <FormattedMessage id="general.permissions" defaultMessage="Permissions" />
                  </div>
                </Col>
                <Col lg={8} className="p-0">
                  {Object.entries(selectedUser.permissions).map(([key, value]) => {
                    if (value.length === 0) return null;

                    const activeValues = value.reduce((acc, curr) => {
                      if (!getValues("permissions")) return acc;
                      if (getValues("permissions")[curr.value]) return acc + 1;
                      return acc;
                    }, 0);

                    return (
                      <Accordion key={key} onSelect={(eventKey) => setActiveAccordion(eventKey)} activeKey={activeAccordion}>
                        <Accordion.Toggle
                          as="div"
                          variant="link"
                          eventKey={key}
                          className={`${activeAccordion !== key ? "mb-3" : "mb-2"}`}
                        >
                          <div className="d-flex align-items-center cursor-pointer">
                            <p className="mb-0 font-weight-bold">{key}</p>
                            <SortIcon
                              className="ml-1"
                              fill="#000"
                              width={8}
                              height={7}
                              transform={`rotate(${activeAccordion === key ? 180 : 0})`}
                            />
                            {activeValues > 0 && (
                              <div className="ml-2">
                                <Badge variant="primary" pill>
                                  {activeValues}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </Accordion.Toggle>
                        {value.map((item) => (
                          <Accordion.Collapse key={item.value} eventKey={key}>
                            <div className="d-flex align-items-center mb-2">
                              <Checkbox
                                providerSections
                                name={`permissions.${item.value}`}
                                checkboxLabel={item.description}
                                disabled={viewOnly}
                              />
                            </div>
                          </Accordion.Collapse>
                        ))}
                      </Accordion>
                    );
                  })}
                  {permissionsError && (
                    <div className="text-danger font-b3 mt-1">
                      <FormattedMessage
                        id="settings.user-role.pleaseSelect1Permission"
                        defaultMessage="Please select at least 1 permission"
                      />
                      .
                    </div>
                  )}
                </Col>
              </div>

              <Row style={{ padding: "16px 0" }}>
                <Col lg={4} className="">
                  <div className="font-b1 font-weight-bold">
                    <FormattedMessage id="settings.user-role.selectedUsers" defaultMessage="Selected users" />
                  </div>
                </Col>
                <Col lg={8}>
                  <div role="presentation">
                    <ExpandableSearch
                      containerClasses="mb-3"
                      value={userSearch}
                      alwaysExpanded
                      onChange={handleSearchUser}
                    />
                    {filteredUsers.length > 0 ? (
                      <div style={{ maxHeight: "192px", overflowY: "scroll" }}>
                        {filteredUsers.map((item) => (
                          <div className="mb-2" key={item.id}>
                            <InputGroup className="checkbox bg-transparent mb-3">
                              <label htmlFor={item.id} className="d-flex align-items-center cursor-pointer mb-0 w-100">
                                <input
                                  id={item.id}
                                  type="checkbox"
                                  className="d-none"
                                  disabled={viewOnly}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const index = filteredUsers.findIndex((user) => user.id === item.id);
                                      filteredUsers[index].is_active = true;
                                      setFilteredUsers([...filteredUsers]);
                                    } else {
                                      const index = filteredUsers.findIndex((user) => user.id === item.id);
                                      filteredUsers[index].is_active = false;
                                      setFilteredUsers([...filteredUsers]);
                                    }
                                  }}
                                  checked={item?.is_active || false}
                                />
                                <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                                <span className="text-primary mr-auto">{`${item.first_name} ${item.last_name}`}</span>
                              </label>
                            </InputGroup>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <span className="text-danger pl-3 pt-5">No Results!</span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              <Row className="mt-4 d-flex justify-content-between">
                <Col className="d-flex justify-content-between">
                  <Button onClick={closeEditRoleDialog}>
                    <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                  </Button>
                  {selectedUser.can_update && !viewOnly && (
                    <div className="d-flex">
                      {canDeleteUserRole && id && (
                        <Button variant="danger" onClick={() => setShowDelete(true)} className="mr-3">
                          <FormattedMessage id="general.delete" defaultMessage="Delete" />
                        </Button>
                      )}
                      <Button isLoading={isSubmitting} onClick={handleSubmit(onSubmit)}>
                        <FormattedMessage id="general.submit" defaultMessage="Submit" />
                      </Button>
                    </div>
                  )}
                  {!id && !viewOnly && (
                    <Button isLoading={isSubmitting} onClick={handleSubmit(onSubmit)}>
                      <FormattedMessage id="general.submit" defaultMessage="Submit" />
                    </Button>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </FormProvider>
      )}
    </Dialog>
  );
};

EditUserRole.propTypes = {
  showEditRoleDialog: PropTypes.bool.isRequired,
  closeEditRoleDialog: PropTypes.func.isRequired,
  selectedEditUserRole: PropTypes.instanceOf(Object),
  isMember: PropTypes.bool,
  id: PropTypes.number,
  count: PropTypes.number.isRequired,
  refetch: PropTypes.func.isRequired,
  viewOnly: PropTypes.bool.isRequired,
};

EditUserRole.defaultProps = {
  selectedEditUserRole: undefined,
  isMember: undefined,
  id: null,
};

export default EditUserRole;
